import { create } from "zustand";

export type AssigneeFilter = "all" | "mine" | "unassigned" | string;

export const SORT_MODES = [
  "manual",
  "newest",
  "oldest",
  "priority",
  "value",
] as const;
export type SortMode = (typeof SORT_MODES)[number];

export const SORT_LABELS: Record<SortMode, string> = {
  manual: "Manual",
  newest: "Recent",
  oldest: "Stale first",
  priority: "Priority",
  value: "Value £",
};

// Current-user store. Board sets this once at mount from the authenticated
// session, then every component that needs the logged-in email reads from
// here via useCurrentUser() or the static getCurrentUser() accessor. Avoids
// prop-drilling the email through every subtree.
type CurrentUserState = {
  email: string;
  set: (email: string) => void;
};
export const useCurrentUserStore = create<CurrentUserState>((set) => ({
  email: "",
  set: (email) => set({ email }),
}));
export const useCurrentUser = (): string =>
  useCurrentUserStore((s) => s.email);
export const getCurrentUser = (): string =>
  useCurrentUserStore.getState().email;

type FiltersState = {
  q: string;
  assignee: AssigneeFilter;
  priorityOnly: boolean;
  includeArchived: boolean;
  sort: SortMode;
  setQ: (q: string) => void;
  setAssignee: (a: AssigneeFilter) => void;
  togglePriority: () => void;
  toggleArchived: () => void;
  setSort: (s: SortMode) => void;
  reset: () => void;
};

// Default sort = "newest" so cards with the most recent activity float to
// the top of every column. Cards needing attention (recent inbound, Emma
// drafts, manual flags) are always near the top of the screen instead of
// hidden below the fold. Filter store is not persisted — every page load
// gets this default until the user picks something else for the session.
export const useFilters = create<FiltersState>((set) => ({
  q: "",
  assignee: "all",
  priorityOnly: false,
  includeArchived: false,
  sort: "newest",
  setQ: (q) => set({ q }),
  setAssignee: (assignee) => set({ assignee }),
  togglePriority: () =>
    set((s) => ({ priorityOnly: !s.priorityOnly })),
  toggleArchived: () =>
    set((s) => ({ includeArchived: !s.includeArchived })),
  setSort: (sort) => set({ sort }),
  reset: () =>
    set({
      q: "",
      assignee: "all",
      priorityOnly: false,
      includeArchived: false,
      sort: "newest",
    }),
}));

// Separate store for the mobile "current stage" switcher. Not persisted; only
// meaningful when the viewport is narrow (<md).
type MobileNavState = {
  currentStage: import("@breeze/shared").Stage;
  setCurrentStage: (s: import("@breeze/shared").Stage) => void;
};

export const useMobileNav = create<MobileNavState>((set) => ({
  currentStage: "lead",
  setCurrentStage: (s) => set({ currentStage: s }),
}));
